import {
  Unit,
  enumUnit,
  enumWeightDisplayUnit,
} from '@rematter/frontend-lib-api-client';
import { settingsGroupQueryAtom } from '@rematter/modules/settings/_graphql/querySettingsGroups';
import { atom } from 'jotai';

import type {
  SettingRecord,
  SettingValueRecord,
  SettingsGroupRecord,
} from '~/modules/settings/types/settings.types';
import { createPermissionAtom } from '~/shared/hooks/usePermission';
import { $accessToken } from '~/shared/providers/auth/identity.localState';

const LANGUAGE_SETTING_ID = 'general.language';
const UNIT_SYSTEM_SETTING_ID = 'general.defaultUnitSystem';
const SALES_DEFAULT_WEIGHT_DISPLAY_UNIT_SETTING_ID =
  'sales.defaultWeightDisplayUnit';
const INVENTORY_DEFAULT_DISPLAY_UNIT_SETTING_ID =
  'inventory.defaultDisplayUnit';
const DEFAULT_LANDING_MODULE_SETTING_ID = 'navigation.defaultLandingModule';

const settingsPermissionAtom = createPermissionAtom('settingsRead');

export const $settingsReadPermission = settingsPermissionAtom((get) => ({
  skip: !get($accessToken),
}));

export const $allSettingsGroups = settingsGroupQueryAtom((get) => ({
  variables: {
    searchTerm: undefined,
    limit: 1000,
  },
  skip: !get($accessToken) || !get($settingsReadPermission).data,
}));

const isUserStringValue = (
  candidate: SettingValueRecord,
): candidate is SettingValueRecord & { value: string } =>
  candidate.scope === 'User' && typeof candidate.value === 'string';

const isOrganizationStringValue = (
  candidate: SettingValueRecord,
): candidate is SettingValueRecord & { value: string } =>
  candidate.scope === 'Organization' && typeof candidate.value === 'string';

const findSetting = (
  groups: SettingsGroupRecord[],
  settingId: string,
): SettingRecord | undefined => {
  for (const group of groups) {
    const setting = group.settings.find(
      (candidate) => candidate.id === settingId,
    );
    if (setting) {
      return setting;
    }
  }
  return undefined;
};

const resolveSettingStringValue = <ResultType>(
  groups: SettingsGroupRecord[],
  settingId: string,
  mapper: (value: string | null) => ResultType,
): ResultType => {
  const setting = findSetting(groups, settingId);
  if (!setting) {
    return mapper(null);
  }

  const organizationValue = setting.values.find(
    isOrganizationStringValue,
  )?.value;
  if (organizationValue) {
    return mapper(organizationValue);
  }

  return mapper(
    typeof setting.defaultValue === 'string' ? setting.defaultValue : null,
  );
};

const unitSystemToUnit = (value: string | null): Unit => {
  if (value === 'Metric') {
    return enumUnit.Kilograms;
  }
  return enumUnit.Pounds;
};

const isWeightDisplayUnit = (
  value: string | null,
): value is keyof typeof enumWeightDisplayUnit =>
  value === enumWeightDisplayUnit.PriceUnit ||
  value === enumWeightDisplayUnit.ScaleUnit;

/**
 * Atom that provides the default unit system setting as a Unit enum
 * Falls back to Pounds (Imperial) if not found
 */
export const $defaultUnitSystemUnit = atom<Unit>((get) => {
  const { data } = get($allSettingsGroups);

  return data?.settingsGroups
    ? resolveSettingStringValue(
        data.settingsGroups,
        UNIT_SYSTEM_SETTING_ID,
        unitSystemToUnit,
      )
    : enumUnit.Pounds;
});

/**
 * Atom that provides the sales default weight display unit as a WeightUnitType
 * Falls back to 'scale' if the setting is unavailable or invalid
 */
export const $salesDefaultWeightDisplayUnit = atom<'scale' | 'price'>((get) => {
  const { data } = get($allSettingsGroups);

  return data?.settingsGroups
    ? resolveSettingStringValue(
        data.settingsGroups,
        SALES_DEFAULT_WEIGHT_DISPLAY_UNIT_SETTING_ID,
        (value) =>
          isWeightDisplayUnit(value) &&
          value === enumWeightDisplayUnit.PriceUnit
            ? 'price'
            : 'scale',
      )
    : 'scale';
});

export type InventoryDisplayUnit =
  | 'ItemUnit'
  | 'Pounds'
  | 'Kilograms'
  | 'MetricTons'
  | 'ShortTons'
  | 'Each';

const validInventoryDisplayUnits: InventoryDisplayUnit[] = [
  'ItemUnit',
  'Pounds',
  'Kilograms',
  'MetricTons',
  'ShortTons',
  'Each',
];

const isInventoryDisplayUnit = (
  value: string | null,
): value is InventoryDisplayUnit =>
  value !== null &&
  validInventoryDisplayUnits.includes(value as InventoryDisplayUnit);

/**
 * Atom that provides the inventory default display unit setting
 * Falls back to 'ItemUnit' (use item's measurement unit) if not found
 */
export const $inventoryDefaultDisplayUnit = atom<InventoryDisplayUnit>(
  (get) => {
    const { data } = get($allSettingsGroups);

    return data?.settingsGroups
      ? resolveSettingStringValue(
          data.settingsGroups,
          INVENTORY_DEFAULT_DISPLAY_UNIT_SETTING_ID,
          (value) => (isInventoryDisplayUnit(value) ? value : 'ItemUnit'),
        )
      : 'ItemUnit';
  },
);

/**
 * Helper to convert InventoryDisplayUnit to Unit enum (or null for ItemUnit)
 */
export const inventoryDisplayUnitToUnit = (
  displayUnit: InventoryDisplayUnit,
): Unit | null => {
  switch (displayUnit) {
    case 'ItemUnit':
      return null;
    case 'Pounds':
      return enumUnit.Pounds;
    case 'Kilograms':
      return enumUnit.Kilograms;
    case 'MetricTons':
      return enumUnit.MetricTons;
    case 'ShortTons':
      return enumUnit.ShortTons;
    case 'Each':
      return enumUnit.Each;
    default:
      return null;
  }
};

export const $userLanguageSetting = atom<string | null>((get) => {
  const { data } = get($allSettingsGroups);
  if (!data?.settingsGroups) return null;

  const setting = findSetting(data.settingsGroups, LANGUAGE_SETTING_ID);
  if (!setting) return null;

  const userValue = setting.values.find(isUserStringValue)?.value;
  if (userValue) return userValue;

  const orgValue = setting.values.find(isOrganizationStringValue)?.value;
  if (orgValue) return orgValue;

  return typeof setting.defaultValue === 'string' ? setting.defaultValue : null;
});

const CALENDAR_COLOR_MODE_SETTING_ID = 'calendar.colorMode';
const CALENDAR_DEFAULT_VIEW_SETTING_ID = 'calendar.defaultView';
const CALENDAR_ENABLED_LAYERS_SETTING_ID = 'calendar.enabledLayers';

const SIDEBAR_MODULE_ORDER_SETTING_ID = 'navigation.sidebarModuleOrder';
const SIDEBAR_SUBSECTION_ORDER_SETTING_ID = 'navigation.sidebarSubsectionOrder';

export const $userSidebarModuleOrder = atom<string[]>((get) => {
  const { data } = get($allSettingsGroups);
  if (!data?.settingsGroups) return [];

  const setting = findSetting(
    data.settingsGroups,
    SIDEBAR_MODULE_ORDER_SETTING_ID,
  );
  if (!setting) return [];

  const userValue = setting.values.find((v) => v.scope === 'User')?.value;
  if (Array.isArray(userValue)) return userValue as string[];

  return [];
});

export const $userSidebarSubsectionOrder = atom<Record<string, string[]>>(
  (get) => {
    const { data } = get($allSettingsGroups);
    if (!data?.settingsGroups) return {};

    const setting = findSetting(
      data.settingsGroups,
      SIDEBAR_SUBSECTION_ORDER_SETTING_ID,
    );
    if (!setting) return {};

    const userValue = setting.values.find((v) => v.scope === 'User')?.value;
    if (userValue && typeof userValue === 'object' && !Array.isArray(userValue))
      return userValue as Record<string, string[]>;

    return {};
  },
);

export const $defaultLandingModule = atom<string | null>((get) => {
  const { data } = get($allSettingsGroups);
  if (!data?.settingsGroups) return null;

  const setting = findSetting(
    data.settingsGroups,
    DEFAULT_LANDING_MODULE_SETTING_ID,
  );
  if (!setting) return null;

  const userValue = setting.values.find((v) => v.scope === 'User')?.value;
  if (typeof userValue === 'string' && userValue !== '') return userValue;

  return null;
});

export const $userCalendarColorMode = atom<string>((get) => {
  const { data } = get($allSettingsGroups);
  if (!data?.settingsGroups) return 'byModule';

  const setting = findSetting(
    data.settingsGroups,
    CALENDAR_COLOR_MODE_SETTING_ID,
  );
  if (!setting) return 'byModule';

  const userValue = setting.values.find(isUserStringValue)?.value;
  if (userValue) return userValue;

  return typeof setting.defaultValue === 'string'
    ? setting.defaultValue
    : 'byModule';
});

export const $userCalendarDefaultView = atom<string>((get) => {
  const { data } = get($allSettingsGroups);
  if (!data?.settingsGroups) return 'dayGridMonth';

  const setting = findSetting(
    data.settingsGroups,
    CALENDAR_DEFAULT_VIEW_SETTING_ID,
  );
  if (!setting) return 'dayGridMonth';

  const userValue = setting.values.find(isUserStringValue)?.value;
  if (userValue) return userValue;

  return typeof setting.defaultValue === 'string'
    ? setting.defaultValue
    : 'dayGridMonth';
});

export const $userCalendarEnabledLayers = atom<Record<string, boolean>>(
  (get) => {
    const defaultLayers: Record<string, boolean> = {
      booking: true,
      load: true,
      salesOrder: true,
      route: true,
      driverCertification: true,
      workOrder: true,
      inboundTolling: true,
      outboundTolling: true,
      dispatchRecord: false,
      invoice: false,
      bill: false,
      inventoryMovement: false,
    };

    const { data } = get($allSettingsGroups);
    if (!data?.settingsGroups) return defaultLayers;

    const setting = findSetting(
      data.settingsGroups,
      CALENDAR_ENABLED_LAYERS_SETTING_ID,
    );
    if (!setting) return defaultLayers;

    const userValue = setting.values.find((v) => v.scope === 'User')?.value;
    if (userValue && typeof userValue === 'object' && !Array.isArray(userValue))
      return userValue as Record<string, boolean>;

    if (
      setting.defaultValue &&
      typeof setting.defaultValue === 'object' &&
      !Array.isArray(setting.defaultValue)
    )
      return setting.defaultValue as Record<string, boolean>;

    return defaultLayers;
  },
);
